import { nanoid } from 'nanoid'
import { z } from 'zod'

// ─── Namespaces ─────────────────────────────────────────────────────────────
//
// Two namespaces only. Realtime = scanner / dashboard news / watchlist columns.
// Historical = history records / ticker stats / comparison columns.
// (The legacy frontend had three: realtime, historyComp, historyFull. We
// collapse the historical pair because the new frontend only ships two
// column catalogs, and there is no value in a per-shape split.)

export const EXPRESSION_NAMESPACES = ['realtime', 'historical'] as const
export const ExpressionNamespaceSchema = z.enum(EXPRESSION_NAMESPACES)
export type ExpressionNamespace = z.infer<typeof ExpressionNamespaceSchema>

// ─── Format & Symbol ────────────────────────────────────────────────────────
//
// Display formatting controls. Values mirror the legacy frontend so existing
// formatters can be ported as-is. If the renderer doesn't recognize a value
// it should fall back to 'auto' / 'none' rather than throw.

export const EXPRESSION_FORMATS = ['auto', 'fixed', 'compact', 'percent'] as const
export const ExpressionFormatSchema = z.enum(EXPRESSION_FORMATS)
export type ExpressionFormat = z.infer<typeof ExpressionFormatSchema>

export const EXPRESSION_SYMBOLS = ['none', 'currency', 'percent', 'multiple'] as const
export const ExpressionSymbolSchema = z.enum(EXPRESSION_SYMBOLS)
export type ExpressionSymbol = z.infer<typeof ExpressionSymbolSchema>

// ─── Constants ──────────────────────────────────────────────────────────────

export const MAX_EXPRESSION_LABEL_LENGTH = 64
export const MAX_EXPRESSION_PLACEHOLDERS = 7
export const LIBRARY_EXPRESSION_PREFIX = 'expr_'

// ─── Core shape ─────────────────────────────────────────────────────────────
//
// `formula` uses placeholder syntax `[[A]]`, `[[B]]`, ... Each placeholder
// letter maps to a column reference in `args`. This is the legacy on-disk
// format and is what the parser/evaluator expects.
//
// Example:
//   formula: '(100 - [[A]]) / (10 * [[B]])'
//   args:    { A: { column: 'session_lp' }, B: { column: 'o_chg' } }

export const ExpressionArgSchema = z.object({
  column: z.string(),
})
export type ExpressionArg = z.infer<typeof ExpressionArgSchema>

const ExpressionBodySchema = z.object({
  label: z.string().min(1).max(MAX_EXPRESSION_LABEL_LENGTH),
  formula: z.string(),
  args: z.record(ExpressionArgSchema),
  format: ExpressionFormatSchema.default('auto'),
  symbol: ExpressionSymbolSchema.default('none'),
})

// Library entry — the user-scoped template the user manages in the
// Expression Library Manager. Persisted (channel TBD).
export const LibraryExpressionSchema = ExpressionBodySchema.extend({
  id: z.string().startsWith(LIBRARY_EXPRESSION_PREFIX),
  namespace: ExpressionNamespaceSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Soft delete. The row stays so references keep resolving; pickers hide it. */
  deletedAt: z.string().optional(),
})
export type LibraryExpression = z.infer<typeof LibraryExpressionSchema>

export const ExpressionLibrarySchema = z.object({
  realtime: z.record(LibraryExpressionSchema),
  historical: z.record(LibraryExpressionSchema),
  ordering: z.object({
    realtime: z.array(z.string()),
    historical: z.array(z.string()),
  }),
})
export type ExpressionLibrary = z.infer<typeof ExpressionLibrarySchema>

export function emptyExpressionLibrary(): ExpressionLibrary {
  return {
    realtime: {},
    historical: {},
    ordering: { realtime: [], historical: [] },
  }
}

// ─── ID helpers ─────────────────────────────────────────────────────────────

export function generateLibraryExpressionId(): string {
  return `${LIBRARY_EXPRESSION_PREFIX}${nanoid()}`
}

export function isLibraryExpressionId(id: string): boolean {
  return id.startsWith(LIBRARY_EXPRESSION_PREFIX)
}

// ─── Validation Result ──────────────────────────────────────────────────────
// Pre-existing schema kept intact for any current consumers.

export const ExpressionValidationSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
  columnRefs: z.array(z.string()),
})
export type ExpressionValidation = z.infer<typeof ExpressionValidationSchema>
